import { useCallback, useRef } from 'react';
import { parse } from 'date-fns';
import { useDealMutations } from './use-deal-mutations';
import { useWorkspaceMembers } from './use-workspace-members';
import { DealPropertiesInput } from '@/lib/graphql/types-and-hooks';
import { isLovablePreview } from '@/lib/utils/environment';

interface PendingUpdate {
  dealId: string;
  properties: Partial<DealPropertiesInput>;
  customProperties?: Record<string, any>;
  timeoutId: NodeJS.Timeout;
}

// Map UI column keys to backend property names. Exported for the field-history
// lookup (src/lib/field-history/field-key.ts), which must ask for the exact
// property this hook writes.
export const COLUMN_TO_PROPERTY_MAP: Record<string, string> = {
  name: 'name',
  stage: 'stage',
  amount: 'amount',       // Special: parse "$50,000" → 50000 (numeric)
  closeDate: 'closeDate', // Special: format to ISO date
  description: 'description',
  priority: 'priority',
  type: 'type',
  forecastCategory: 'forecastCategory',
  note: 'note',
  lastStep: 'lastActivityText',
  nextStep: 'nextActivityText',
  // company and contacts handled specially via accountIds/contactIds
};

export const useDebouncedDealUpdate = (debounceMs: number = 500) => {
  const { updateDeal } = useDealMutations();
  const { members } = useWorkspaceMembers();
  const pendingUpdatesRef = useRef<Map<string, PendingUpdate>>(new Map());

  const scheduleUpdate = useCallback(
    (dealId: string, columnKey: string, value: string) => {
      if (isLovablePreview()) {
        return; // Skip updates in preview mode
      }

      // Cancel existing timeout for this deal
      const existing = pendingUpdatesRef.current.get(dealId);
      if (existing?.timeoutId) {
        clearTimeout(existing.timeoutId);
      }

      // Build properties object
      let properties: Partial<DealPropertiesInput> = existing?.properties || {};
      let customProperties: Record<string, any> = existing?.customProperties || {};

      // Check if this is a custom field (starts with custom_)
      if (columnKey.startsWith('custom_')) {
        const fieldName = columnKey.replace('custom_', '');

        // For numeric/currency fields, convert string to number if applicable
        // saveRaw: true pattern — prevents string formatting from breaking numeric storage
        let fieldValue: any = value;
        if (value && value !== '' && !isNaN(Number(value))) {
          if (/^-?\d*\.?\d+$/.test(value.trim())) {
            fieldValue = Number(value);
          }
        }

        customProperties = { ...customProperties, [fieldName]: fieldValue };
      } else if (columnKey === 'amount') {
        // Parse "$50,000" → 50000 (remove $, commas)
        const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
        if (!isNaN(numericValue)) {
          properties = { ...properties, amount: numericValue };
        }
      } else if (columnKey === 'closeDate') {
        // Convert display format "Mar 25, 2026" → ISO string for DateTime field
        try {
          const parsed = parse(value, 'MMM d, yyyy', new Date());
          if (!isNaN(parsed.getTime())) {
            properties = { ...properties, closeDate: parsed.toISOString() };
          }
        } catch { /* ignore invalid dates */ }
      } else if (columnKey === 'owner') {
        // Resolve display name → member ID for ownerId field
        const member = members.find(m => m.name === value);
        if (member) {
          properties = { ...properties, ownerId: member.id };
        }
      } else {
        // Standard property mapping
        const propertyKey = COLUMN_TO_PROPERTY_MAP[columnKey];
        if (propertyKey) {
          properties = { ...properties, [propertyKey]: value };
        }
      }

      // Schedule the update
      const timeoutId = setTimeout(async () => {
        const updatePayload: any = {
          id: dealId,
          properties: properties, // Always send properties (even if empty) — matches coffee-ui pattern
        };
        if (Object.keys(customProperties).length > 0) {
          updatePayload.customProperties = customProperties;
        }
        await updateDeal(updatePayload);
        pendingUpdatesRef.current.delete(dealId);
      }, debounceMs);

      pendingUpdatesRef.current.set(dealId, { dealId, properties, customProperties, timeoutId });
    },
    [updateDeal, debounceMs, members]
  );

  return { scheduleUpdate };
};
